import React, { forwardRef } from 'react';
import { cn } from './cn';

export type FieldSize = 'sm' | 'md' | 'lg';

const BASE =
  'w-full min-w-0 bg-surface-1 text-ink placeholder:text-ink-faint border border-border rounded-lg ' +
  'transition-[box-shadow,border-color] duration-180 ease-premium ' +
  'focus:border-brand focus:shadow-ring-brand focus:outline-none ' +
  'disabled:bg-surface-2 disabled:text-ink-faint disabled:cursor-not-allowed';

const SIZE: Record<FieldSize, string> = {
  sm: 'h-8 px-2.5 text-body-sm',
  md: 'h-10 px-3 text-body-md',
  lg: 'h-12 px-4 text-body-lg',
};

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  inputSize?: FieldSize;
  leadingIcon?: React.ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { inputSize = 'md', leadingIcon, invalid, className, ...rest },
  ref,
) {
  if (leadingIcon) {
    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint h-4 w-4 flex items-center pointer-events-none" aria-hidden>
          {leadingIcon}
        </span>
        <input
          ref={ref}
          className={cn(
            BASE,
            SIZE[inputSize],
            'pl-9',
            invalid && 'border-sev-critical focus:border-sev-critical focus:shadow-[0_0_0_3px_rgb(var(--sev-critical)/0.18)]',
            className,
          )}
          {...rest}
        />
      </div>
    );
  }
  return (
    <input
      ref={ref}
      className={cn(
        BASE,
        SIZE[inputSize],
        invalid && 'border-sev-critical focus:border-sev-critical focus:shadow-[0_0_0_3px_rgb(var(--sev-critical)/0.18)]',
        className,
      )}
      {...rest}
    />
  );
});

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, rows = 4, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        BASE,
        'py-2.5 px-3 text-body-md resize-y min-h-[5rem]',
        invalid && 'border-sev-critical focus:border-sev-critical focus:shadow-[0_0_0_3px_rgb(var(--sev-critical)/0.18)]',
        className,
      )}
      {...rest}
    />
  );
});

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  selectSize?: FieldSize;
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, selectSize = 'md', invalid, children, ...rest },
  ref,
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          BASE,
          SIZE[selectSize],
          'appearance-none pr-9',
          invalid && 'border-sev-critical',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle" aria-hidden>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </span>
    </div>
  );
});

/** Paired label for consistent form layouts. */
export const Field: React.FC<{
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ label, htmlFor, hint, error, optional, children, className }) => (
  <div className={cn('flex flex-col gap-1.5', className)}>
    <label htmlFor={htmlFor} className="text-body-sm font-medium text-ink flex items-center gap-2">
      {label}
      {optional && <span className="text-caption font-normal text-ink-faint">Optional</span>}
    </label>
    {children}
    {(hint || error) && (
      <p className={cn('text-caption', error ? 'text-sev-critical-ink' : 'text-ink-subtle')}>
        {error || hint}
      </p>
    )}
  </div>
);
